import React, { useContext, useMemo, useState } from 'react';
import { EntityStatus } from '../../refData/EntityStatus';
import styles from '../../styles/cr.module.scss';
import { IUser, User, ISpecificEntityFormProps, EntityValidations } from '../../types';
import { DataContext } from '../DataContext';
import { CrDropdown } from '../cr/CrDropdown';
import { CrTextField } from '../cr/CrTextField';
import { EntityForm } from '../EntityForm';
import { isValidEmailAddress, isValidGuid } from '../../services';
import { IntegrationContext } from '../IntegrationContext';
import { CrCheckbox } from '../cr/CrCheckbox';
import { AuthenticationTypes } from '../../refData/AuthenticationTypes';
import axios from 'axios';

export class UserValidations extends EntityValidations {
    public Username: string = null;
    public UserTitle: string = null;
    public Firstname: string = null;
    public Surname: string = null;
    public EmailAddress: string = null;
    public Password: string = null;
    public AuthenticationType = null;
}

interface UserFormProps extends ISpecificEntityFormProps {
    customerRef: string;
    environment: string;
}

export const UserForm = (props: UserFormProps): React.ReactElement => {
    const { dataServices: { userService }, loadLookupData: { entityStatuses } } = useContext(DataContext);

    //const [passwordAction, setPasswordAction] = useState<number | string>(1);
    const [emailExistsCoreDb, setEmailExistsCoreDb] = useState<boolean>(null);

    useMemo(() => entityStatuses(), [entityStatuses]);

    const validateEntity = async (user: IUser): Promise<UserValidations> => {
        const errors = new UserValidations();

        if (user.UserTitle == null || user.UserTitle.trim() === '') {
            errors.UserTitle = 'Title is required';
            errors.Valid = false;
        }
        else {
            errors.UserTitle = null;
        }

        if (user.Firstname == null || user.Firstname.trim() === '') {
            errors.Firstname = 'First name is required';
            errors.Valid = false;
        }
        else {
            errors.Firstname = null;
        }

        if (user.Surname == null || user.Surname.trim() === '') {
            errors.Surname = 'Surname is required';
            errors.Valid = false;
        }
        else {
            errors.Surname = null;
        }

        if ((user.EmailAddress == null || user.EmailAddress === '')) {
            errors.EmailAddress = 'Email address is required';
            errors.Valid = false;
        } else if (!isValidEmailAddress(user.EmailAddress)) {
            errors.EmailAddress = 'Email address must be a valid email address';
            errors.Valid = false;
        }
        else {
            errors.EmailAddress = null;
        }

        if (user.AuthenticationTypeID === null) {
            errors.AuthenticationType = 'Authentication Type is required';
            errors.Valid = false;
        }
        else {
            errors.AuthenticationType = null;
        }

        if (emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 && (user.PasswordActionID === 1 || user.PasswordActionID === 3)) {
            if (user.Password == null || user.Password === '') {
                errors.Password = 'Password is required';
                errors.Valid = false;
            }
            else
                errors.Password = null;

        }
        else {
            errors.Password = null;
        }

        return errors;
    };

    const passwordActions = [
        { key: 1, text: 'Set Password' },
        { key: 2, text: 'None' },
        { key: 3, text: 'Reset Password' },
    ];

    const getPasswordActions = () => {
        if (emailExistsCoreDb === false)
            return passwordActions.filter(x => x.key === 1);
        else if (emailExistsCoreDb === true) {
            return passwordActions.filter(x => x.key > 1);
        }
        return [];
    }

    const checkEmailOnCoreDb = async (email: string): Promise<boolean> => {

        console.log('call api to check if email exist in core db');
        const emailExist: boolean = await userService.emailExistOnCoreUsers(email);

        setEmailExistsCoreDb(emailExist);
        return emailExist;
    }

    return (
        <EntityForm<IUser, UserValidations>
            {...props}
            entityName="Customer Super User"
            renderFormFields={({ changeTextField, changeNumberField, changeCheckbox, changeDropdown, changeStatusDropdown, clearField }, formState) => {
                const { FormData: user, ValidationErrors: errors } = formState;

                const updateDisplayName = (
                    firstname: string,
                    surname: string,
                    changeTextField: (value: string, field: string) => void
                ) => {
                    const displayName = `${firstname || ''} ${surname || ''}`.trim();
                    changeTextField(displayName, 'Title');
                };

                return (
                    <div>

                        <CrDropdown
                            label="Title"
                            className={styles.formField}
                            options={[
                                { key: "Mr", text: "Mr" },
                                { key: "Mrs", text: "Mrs" },
                                { key: "Ms", text: "Ms" },
                                { key: "Dr", text: "Dr" }
                            ]}
                            selectedKey={user.UserTitle}
                            onChange={(_, o) => changeDropdown(o, 'UserTitle')}
                            required={true}
                            errorMessage={errors.UserTitle}
                        />

                        <CrTextField
                            label="First name"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.Firstname}
                            maxLength={255}
                            value={user.Firstname || ''}
                            onChange={(ev, newValue) => {
                                changeTextField(newValue, 'Firstname');
                                updateDisplayName(newValue, user.Surname, changeTextField);
                            }}
                        />

                        <CrTextField
                            label="Surname"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.Surname}
                            maxLength={255}
                            value={user.Surname || ''}
                            onChange={(ev, newValue) => {
                                changeTextField(newValue, 'Surname');
                                updateDisplayName(user.Firstname, newValue, changeTextField);
                            }}
                        />

                        <CrTextField
                            label="Email address"
                            className={styles.formField}
                            required={true}
                            maxLength={255}
                            placeholder="E.g. john.smith@yourdomain.com"
                            value={user.EmailAddress || ''}
                            onChange={(ev, newValue) => changeTextField(newValue, 'EmailAddress')}
                            errorMessage={errors.EmailAddress}
                        />

                        <CrTextField
                            label="Phone"
                            className={styles.formField}
                            maxLength={100}
                            value={user.Phone || ''}
                            onChange={(ev, newValue) => changeTextField(newValue, 'Phone')}
                        />

                        <CrDropdown
                            label="Authentication Type"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.AuthenticationType}
                            options={[{ key: AuthenticationTypes.ZedX, text: 'ZedX' }, { key: AuthenticationTypes.Azure, text: 'Azure' }]}
                            selectedKey={user.AuthenticationTypeID}
                            onChange={(_, o) => {
                                changeDropdown(o, 'AuthenticationTypeID', null, async (u) => {
                                    if (u.AuthenticationTypeID === AuthenticationTypes.ZedX) {
                                        if (!isValidEmailAddress(u.EmailAddress)) {
                                            console.log('invalid email address');
                                        }
                                        else {
                                            const emailExist = await checkEmailOnCoreDb(u.EmailAddress);
                                            if (emailExist == true) {
                                                changeNumberField(2, 'PasswordActionID');//default None
                                            }

                                        }

                                    }

                                });

                            }}
                        />

                        {emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 &&
                            <CrDropdown
                                label="Password Actions"
                                className={styles.formField}
                                options={getPasswordActions()}
                                selectedKey={user.PasswordActionID}
                                onChange={(_, o) => {
                                    console.log(o.key);
                                    changeDropdown(o, 'PasswordActionID')
                                }}
                            />
                        }

                        {emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 && (user.PasswordActionID === 1 || user.PasswordActionID === 3) &&
                            <CrTextField
                                label="Password"
                                type='password'
                                canRevealPassword
                                className={styles.formField}
                                required={true}
                                maxLength={255}
                                autoComplete="new-password"
                                value={user.Password}
                                onChange={(ev, newValue) => changeTextField(newValue, 'Password')}
                                errorMessage={errors.Password}
                            />
                        }

                        {user.ID !== 0 &&
                            <CrDropdown
                                label="Account status"
                                className={styles.formField}
                                required={true}
                                options={[{ key: EntityStatus.Open, text: 'Enabled' }, { key: EntityStatus.Closed, text: 'Disabled' }]}
                                selectedKey={user.EntityStatusID}
                                onChange={(_, o) => changeStatusDropdown(o, 'EntityStatusID')}
                            />
                        }
                    </div>
                );
            }}
            loadEntity={(id) => {
                return userService.readCustomerUser(props.customerRef, props.environment, id);
            }}
            onAfterLoad={async (entity) => {
                console.log('on after load', entity);
                await checkEmailOnCoreDb(entity.EmailAddress);
            }}
            loadNewEntity={() => new User(props.customerRef)}
            loadEntityValidations={() => new UserValidations()}
            onValidateEntity={validateEntity}
            onCreate={u => userService.addCustomerUser(props.environment, u)}
            onUpdate={u => userService.updateCustomerUser(u.ID, props.customerRef, props.environment, u)}
            closeEntityConfirm={{
                header: `Are you sure you want to disable this user account?`,
                text: `All the permissions for this user will be deleted. `
                    + `If you need to re-enable this user account, you will also need to set up its user permissions.`
            }}
        />
    );
};